import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { store } from 'Models/Store';
import { stringNotEmpty } from 'Util/TypeGuards';
import If from 'Views/Components/If/If';
import CapacityStateTooltip from './CapacityStateTooltip';
import ClosedCapacityInfo from './ClosedCapacityInfo';

interface SpacesRemainingTagProps {
	space: number;
	capacityName?: string;
	icon?: JSX.Element;
	// if this is set to false, it will just show an indicator of
	// whether or not there is spaces available (green or red)
	showCapacityRemaining?: boolean;
	disabled?: boolean;
	showName?: boolean;
	closed?: boolean;
	// When set, the tag becomes a hover/tap target that shows this label in a tooltip
	// (used to explain what each capacity icon represents).
	tooltipText?: string;
	// When set, a staff member sees a negative figure as how far the capacity is oversold, instead of
	// the red dot a merely full capacity shows.
	showOversold?: boolean;
	/**
	 * Put a mark beside `(closed)` explaining what closed means for this booker.
	 *
	 * <p>Opt-in. The word alone reads as the sailing being full, which has a different answer, and
	 * only the surfaces that sell a sailing are in a position to say "ring us instead".</p>
	 */
	explainClosed?: boolean;
	/**
	 * Replace the name tooltip with one describing the capacity's state.
	 *
	 * <p>For a reader who can book past a full sailing. The name is already printed on the chip, so
	 * repeating it in a tooltip spends the only hover the chip has on something already on screen —
	 * whereas whether this particular capacity is shut or merely full is the thing the chip cannot
	 * fit and the thing that decides what they do next.</p>
	 */
	explainState?: boolean;
	/**
	 * The figure before it was rounded down, for the one case that needs it.
	 *
	 * <p>Vehicle space is measured in lengths or weights, so it is rarely a whole number, and the
	 * chip floors it because nobody can book four fifths of a car. On an OVERSOLD sailing that
	 * rounding runs the wrong way for the person deciding what to do about it: 1.74 car lengths
	 * past full floors to `-2`, which is more than is actually on the vessel. Only used below zero,
	 * and only for a reader who can act on it.</p>
	 */
	exactSpace?: number;
	/**
	 * Why a closed capacity is closed, where it is not the usual reason.
	 *
	 * <p>Becomes the tooltip, and the chip keeps its red dot rather than reading `(closed)` with the
	 * generic "contact staff" explanation — see `isWalkOnNotOffered`, the case it exists for.</p>
	 */
	closedReason?: string;
}

// TODO capacity - after model change this takes a capacity type (which will have the icon, name, etc. on it)
export function SpacesRemainingTag({
	space,
	capacityName,
	icon,
	showCapacityRemaining = true,
	disabled = false,
	showName = false,
	closed = false,
	tooltipText,
	showOversold = false,
	explainClosed = false,
	explainState = false,
	exactSpace,
	closedReason,
}: SpacesRemainingTagProps) {
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const { formatMessage } = useIntl();
	const oversold = showOversold && store.isStaff && showCapacityRemaining && !closed && space < 0;

	/** Nothing left — as opposed to shut, which is a different state with a different answer. */
	const soldOut = !closed && space <= 0;


	/*
		What this capacity's state is, where the caller asked for that instead of its name.

		Closed first: a capacity can be both, and "shut" is the one that cannot be worked around.
		Neither applies to a capacity with room, which then has no tooltip at all rather than one
		saying what the chip already says.
	*/
	const stateTooltip = (() => {
		if (!explainState) {
			return undefined;
		}
		if (closed && stringNotEmpty(closedReason)) {
			return closedReason;
		}
		if (closed) {
			return formatMessage(
				{ id: 'capacity-tag__closed__tooltip', defaultMessage: '{capacity} is closed for bookings' },
				{ capacity: capacityName ?? 'This capacity' },
			);
		}
		if (soldOut) {
			return formatMessage(
				{ id: 'capacity-tag__sold-out__tooltip', defaultMessage: '{capacity} is sold out' },
				{ capacity: capacityName ?? 'This capacity' },
			);
		}
		return undefined;
	})();

	const resolvedTooltip = explainState ? stateTooltip : (closed && closedReason) || tooltipText;

	// `(closed)` and its explanation are the generic reason; a specific one is in the tooltip instead.
	const showClosedWord = showName && closed && !store.isStaff && !stringNotEmpty(closedReason);

	/*
		The figure itself, however small or negative, for a reader who can book past it.

		Everyone else gets a dot at this point, because "none left" is the whole answer when you
		cannot have it anyway. A manager is deciding whether to overbook, and the number is the
		thing they are weighing — `0` is a different decision from `-2`, and a chip showing neither
		tells them nothing. Note `availability.vehicle` is already floored, so this is a whole
		number: a sailing with 0.8 of a car space left reads `0`.
	*/
	/*
		...but only where the figure is not already on the chip.

		A CLOSED capacity with room left takes the first branch below — `space > 0` and a staff
		reader — and this branch as well, because it is entered on `closed` alone. Both printed, and
		a shut sailing with 38 car spaces came out reading `Vehicles 38 38`.
	*/
	const figureAlreadyShown = showCapacityRemaining && space > 0 && (!closed || store.isStaff);

	/*
		The real figure, negatives included — staff only.

		A customer has no use for `-2`: they cannot overbook, and a sailing two vehicles past full is
		simply full to them. It is the number a staff member is weighing.
	*/
	const showExactFigure = explainState && store.isStaff && showCapacityRemaining && !figureAlreadyShown;


	/*
		Below zero, to two places; at or above it, the whole number.

		`0` is the honest answer for a sailing with four fifths of a space left — there is no room
		for a car — but `-2` is not the honest answer for one 1.74 lengths over, because the two
		decimals are the size of the problem.
	*/
	const exactFigure = space < 0 ? (exactSpace ?? space).toFixed(2) : space;

	// Not closed,
	const tag = (
		<div
			className={classNames('spaces-remaining-tag__container', {
				disabled,
				/*
					A capacity that is shut rather than merely empty.

					Only where the tag actually says so. Without a name it falls back to the red dot,
					which means "none left" — and a staff member sees the real figure, because they
					can still book it. A modifier rather than a `:has()` on the text, since `(closed)`
					is a bare text node with nothing to select.
				*/
				'spaces-remaining-tag__container--closed': closed,
				// Closed for a reason of its own, which keeps the red dot — see `closedReason`.
				'spaces-remaining-tag__container--closed-reason': closed && stringNotEmpty(closedReason),
				// Nothing left. Styled where it is used — the ticket card greys it; the schedule,
				// which does not set `explainState`, is untouched.
				'spaces-remaining-tag__container--sold-out': soldOut,
			})}
		>
			{icon}
			{showName && <p>{capacityName}</p>}
			<div className="spaces-remaining-tag__capacity-info">
				{/* If we want to show the capacity and there is space remaining and it's not closed */}
				{/* Show spaces remaining */}
				<If condition={showCapacityRemaining && space > 0 && (!closed || store.isStaff)}>{space}</If>
				{/* Oversold: say by how much, so it does not read as merely full */}
				<If condition={oversold}>
					<span className="spaces-remaining-tag__oversold">{`${-space} over`}</span>
				</If>
				{/* If we don't want to show the capacity or there is no space remaining or it is closed */}
				<If condition={!oversold && (!showCapacityRemaining || space <= 0 || closed)}>
					{/* Manager+ reads the number; see `showExactFigure`. */}
					<If condition={showExactFigure}>
						<span className="spaces-remaining-tag__exact">{exactFigure}</span>
					</If>
					{/* Don't show name, show indicator (if closed) */}
					<If condition={!showExactFigure && !showClosedWord}>
						<SpacesRemainingTagWithDotIndicator space={closed ? 0 : space} />
					</If>
					{/* Show long name, show "closed" */}
					<If condition={showClosedWord}>
						(closed)
						<If condition={explainClosed}>
							<ClosedCapacityInfo />
						</If>
					</If>
				</If>
			</div>
		</div>
	);

	if (!stringNotEmpty(resolvedTooltip)) {
		return tag;
	}

	/*
		The state tooltip is portalled; the name tooltip below is not.

		`.custom-tooltip` draws its panel absolutely inside the chip, which is fine on the ferry
		schedule and wrong in the booking wizard, where the fare table, the chosen card and the
		phone's sailing card each clip their contents to a rounded box — the panel was cut off
		mid-sentence. The name tooltip keeps the old mechanism because the surfaces that use it do
		not clip.
	*/
	if (explainState) {
		return <CapacityStateTooltip text={resolvedTooltip}>{tag}</CapacityStateTooltip>;
	}

	// Wrap the tag so hovering (desktop) or tapping (touch) reveals a label explaining the capacity.
	return (
		<span
			className="custom-tooltip with-custom-trigger spaces-remaining-tag__tooltip"
			role="button"
			tabIndex={0}
			aria-label={resolvedTooltip}
			onClick={e => {
				// Stop the tap from bubbling up and selecting the leg/trip behind the tag.
				e.stopPropagation();
				setTooltipOpen(open => !open);
			}}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					e.stopPropagation();
					setTooltipOpen(open => !open);
				}
			}}
			onMouseLeave={() => setTooltipOpen(false)}
			onBlur={() => setTooltipOpen(false)}
		>
			{tag}
			<span
				className={classNames('tooltiptext', 'bottom-right')}
				style={tooltipOpen ? { visibility: 'visible', opacity: 1 } : undefined}
			>
				{resolvedTooltip}
			</span>
		</span>
	);
}

interface SpacesRemainingTagWithDotIndicatorProps {
	space: number;
}

function SpacesRemainingTagWithDotIndicator({ space }: SpacesRemainingTagWithDotIndicatorProps) {
	let dotIndicatorColor = 'green';

	if (space <= 0) {
		dotIndicatorColor = 'red';
	}

	return (
		<div
			className={`spaces-remaining-tag__dot-indicator spaces-remaining-tag__dot-indicator--${dotIndicatorColor}`}
		/>
	);
}
